import { ComponentDefinition, ComponentType } from '../types';

export const COMPONENT_LIBRARY: ComponentDefinition[] = [
  // ─── Sources ──────────────────────────────────────────────────────────────
  {
    type: 'photon-source',
    category: 'source',
    label: 'Photon Source',
    icon: '💡',
    description: 'Emits individual photons one at a time.',
    tooltip:
      'A photon is a tiny packet of light. We send them one at a time to see quantum effects!',
    ageMode: ['beginner', 'intermediate', 'advanced'],
    defaultProperties: { rate: 100, wavelengthNm: 532 },
    inputs: [],
    outputs: [{ id: 'out', label: 'Photon', type: 'quantum' }],
    color: '#f59e0b',
    bgColor: '#fef3c7',
  },
  {
    type: 'electron-source',
    category: 'source',
    label: 'Electron Source',
    icon: '⚡',
    description: 'Fires electrons at a target.',
    tooltip:
      'Electrons also behave like waves! They can show interference just like light.',
    ageMode: ['intermediate', 'advanced'],
    defaultProperties: { rate: 100, energyEv: 50 },
    inputs: [],
    outputs: [{ id: 'out', label: 'Electron', type: 'quantum' }],
    color: '#6366f1',
    bgColor: '#ede9fe',
  },
  {
    type: 'qubit-source',
    category: 'source',
    label: 'Qubit |0⟩',
    icon: '🔵',
    description: 'Creates a qubit in the |0⟩ state.',
    tooltip:
      'A qubit is a quantum bit. Unlike a normal bit (0 or 1), it can be in BOTH states at once!',
    ageMode: ['advanced'],
    defaultProperties: { initialState: 0 },
    inputs: [],
    outputs: [{ id: 'out', label: 'Qubit', type: 'quantum' }],
    color: '#3b82f6',
    bgColor: '#dbeafe',
  },

  // ─── Devices ──────────────────────────────────────────────────────────────
  {
    type: 'double-slit',
    category: 'device',
    label: 'Double Slit',
    icon: '🚪',
    description: 'Two tiny openings that create interference.',
    tooltip:
      'When a particle passes through two slits at once, it interferes with itself – creating a striped pattern on the screen.',
    ageMode: ['beginner', 'intermediate', 'advanced'],
    defaultProperties: { slitSeparation: 60, slitWidth: 10 },
    inputs: [{ id: 'in', label: 'Particle', type: 'quantum' }],
    outputs: [{ id: 'out', label: 'Wave', type: 'quantum' }],
    color: '#8b5cf6',
    bgColor: '#ede9fe',
  },
  {
    type: 'beam-splitter',
    category: 'device',
    label: 'Beam Splitter',
    icon: '◈',
    description: 'Splits a photon into two probability paths.',
    tooltip:
      'This half-mirror sends the photon in two directions at once! It creates a superposition of "transmitted" and "reflected".',
    ageMode: ['intermediate', 'advanced'],
    defaultProperties: { reflectivity: 50 },
    inputs: [{ id: 'in', label: 'Photon', type: 'quantum' }],
    outputs: [
      { id: 'transmitted', label: 'Transmitted', type: 'quantum' },
      { id: 'reflected', label: 'Reflected', type: 'quantum' },
    ],
    color: '#06b6d4',
    bgColor: '#cffafe',
  },
  {
    type: 'mirror',
    category: 'device',
    label: 'Mirror',
    icon: '🪞',
    description: 'Reflects a photon path.',
    tooltip:
      'A mirror redirects the photon. Used in interferometer setups to bring paths back together.',
    ageMode: ['intermediate', 'advanced'],
    defaultProperties: { angle: 45 },
    inputs: [{ id: 'in', label: 'Photon', type: 'quantum' }],
    outputs: [{ id: 'out', label: 'Photon', type: 'quantum' }],
    color: '#64748b',
    bgColor: '#f1f5f9',
  },
  {
    type: 'phase-shifter',
    category: 'device',
    label: 'Phase Shifter',
    icon: '〜',
    description: 'Shifts the quantum phase of a particle.',
    tooltip:
      'Phase is like the "timing" of a wave. Shifting the phase can cause constructive or destructive interference!',
    ageMode: ['intermediate', 'advanced'],
    defaultProperties: { phaseDeg: 90 },
    inputs: [{ id: 'in', label: 'Photon', type: 'quantum' }],
    outputs: [{ id: 'out', label: 'Photon', type: 'quantum' }],
    color: '#ec4899',
    bgColor: '#fce7f3',
  },
  {
    type: 'hadamard-gate',
    category: 'device',
    label: 'Hadamard Gate',
    icon: 'H',
    description: 'Creates superposition from a definite state.',
    tooltip:
      'The Hadamard gate is like a quantum coin flip! It puts the qubit into an equal mix of 0 and 1.',
    ageMode: ['advanced'],
    defaultProperties: {},
    inputs: [{ id: 'in', label: 'Qubit', type: 'quantum' }],
    outputs: [{ id: 'out', label: 'Qubit', type: 'quantum' }],
    color: '#10b981',
    bgColor: '#d1fae5',
  },
  {
    type: 'measurement-detector',
    category: 'device',
    label: 'Which-Path Detector',
    icon: '👁',
    description: 'Detects which slit a particle uses.',
    tooltip:
      'Watching which slit the particle goes through collapses its superposition. The interference pattern disappears!',
    ageMode: ['beginner', 'intermediate', 'advanced'],
    defaultProperties: { active: true },
    inputs: [{ id: 'in', label: 'Particle', type: 'quantum' }],
    outputs: [{ id: 'out', label: 'Particle', type: 'quantum' }],
    color: '#ef4444',
    bgColor: '#fee2e2',
  },
  {
    type: 'polarizer',
    category: 'device',
    label: 'Polarizer',
    icon: '⊞',
    description: 'Filters photons by their polarization direction.',
    tooltip:
      'Light waves vibrate in different directions. A polarizer only lets through light vibrating in one direction.',
    ageMode: ['intermediate', 'advanced'],
    defaultProperties: { angleDeg: 0 },
    inputs: [{ id: 'in', label: 'Photon', type: 'quantum' }],
    outputs: [{ id: 'out', label: 'Photon', type: 'quantum' }],
    color: '#f97316',
    bgColor: '#ffedd5',
  },

  // ─── Outputs ──────────────────────────────────────────────────────────────
  {
    type: 'detector-screen',
    category: 'output',
    label: 'Detector Screen',
    icon: '📺',
    description: 'Shows where particles land over time.',
    tooltip:
      'Each particle leaves a dot on the screen. After many particles, a pattern emerges – wave-like stripes or two blobs.',
    ageMode: ['beginner', 'intermediate', 'advanced'],
    defaultProperties: { width: 300, height: 200 },
    inputs: [{ id: 'in', label: 'Particle', type: 'quantum' }],
    outputs: [],
    color: '#1d4ed8',
    bgColor: '#dbeafe',
  },
  {
    type: 'probability-graph',
    category: 'output',
    label: 'Probability Graph',
    icon: '📊',
    description: 'Bar chart of measurement outcomes.',
    tooltip:
      'This graph shows how likely each outcome is. In quantum mechanics, outcomes are never certain – only probable!',
    ageMode: ['intermediate', 'advanced'],
    defaultProperties: {},
    inputs: [{ id: 'in', label: 'Signal', type: 'quantum' }],
    outputs: [],
    color: '#059669',
    bgColor: '#d1fae5',
  },
  {
    type: 'measurement-counter',
    category: 'output',
    label: 'Measurement Counter',
    icon: '🔢',
    description: 'Counts how many times 0 or 1 was measured.',
    tooltip:
      'Run the experiment many times and count the results. Is it always 50/50? Try different gates and see!',
    ageMode: ['advanced'],
    defaultProperties: {},
    inputs: [{ id: 'in', label: 'Qubit', type: 'quantum' }],
    outputs: [],
    color: '#7c3aed',
    bgColor: '#ede9fe',
  },
];

export function getComponentDef(
  type: ComponentType
): ComponentDefinition | undefined {
  return COMPONENT_LIBRARY.find((c) => c.type === type);
}
